/**
 * 商户模块：所属行业 / 合作意向 / 加盟身份
 * 数据来自 sys_enum（GET /shop/issueorder.enum/list），失败或空列表时使用本地兜底。
 */

import request from '@/utils/request'

/** 与后台 jjjshop_sys_enum.main_key 一致 */
export const SUPPLIER_ENUM_MAIN_KEYS = {
  industry: 'supplier_industry_code',
  cooperativePurpose: 'supplier_cooperative_purpose_type',
  franchiseeType: 'supplier_franchisee_type',
}

export const FALLBACK_INDUSTRY_OPTIONS = [
  { label: '银行', value: 'bank' },
  { label: '投资公司', value: 'investment' },
  { label: '资产管理公司', value: 'asset_management' },
  { label: '典当行', value: 'pawn' },
  { label: '拍卖行', value: 'auction' },
  { label: '融资租赁', value: 'leasing' },
]

export const FALLBACK_COOPERATIVE_PURPOSE_OPTIONS = [
  { label: '对单服务', value: 1 },
  { label: '入驻开店', value: 2 },
  { label: '线下加盟', value: 3 },
  { label: '生活服务', value: 4 },
]

export const FALLBACK_FRANCHISEE_TYPE_OPTIONS = [
  { label: '无', value: 0 },
  { label: '联盟门店', value: 1 },
  { label: '标准门店', value: 2 },
  { label: '旗舰门店', value: 3 },
  { label: '区域合伙人', value: 4 },
]

const COOP_FALLBACK_MAP = { 0: '-', 1: '对单服务', 2: '入驻开店', 3: '线下加盟', 4: '生活服务' }
const FRANCHISEE_FALLBACK_MAP = { 0: '无', 1: '联盟门店', 2: '标准门店', 3: '旗舰门店', 4: '区域合伙人' }

function mapEnumRowsToOptions(rows, numericValue) {
  if (!rows || !rows.length) return []
  return rows.map(row => {
    let v = row.enum_key
    if (numericValue) {
      const n = parseInt(String(row.enum_key), 10)
      v = Number.isNaN(n) ? row.enum_key : n
    }
    return { label: row.enum_value || String(row.enum_key), value: v }
  })
}

/** 加盟身份：若枚举表未维护「无」，则补上 0 */
function ensureFranchiseeNoneOption(options) {
  const none = FALLBACK_FRANCHISEE_TYPE_OPTIONS.find(x => x.value === 0)
  if (!options || !options.length) return [...FALLBACK_FRANCHISEE_TYPE_OPTIONS]
  if (!none) return options
  const hasZero = options.some(o => Number(o.value) === 0)
  if (hasZero) return options
  return [none, ...options]
}

/**
 * 并行拉取三类枚举；任一类接口失败或返回空则该类用兜底列表
 * @param {typeof request} req 默认 @/utils/request
 */
export function fetchSupplierEnumOptions(req = request) {
  const tasks = [
    { mainKey: SUPPLIER_ENUM_MAIN_KEYS.industry, numeric: false },
    { mainKey: SUPPLIER_ENUM_MAIN_KEYS.cooperativePurpose, numeric: true },
    { mainKey: SUPPLIER_ENUM_MAIN_KEYS.franchiseeType, numeric: true },
  ]
  return Promise.all(
    tasks.map(({ mainKey }) =>
      req._get('/shop/issueorder.enum/list', { main_key: mainKey }).then(res => {
        return (res.data && res.data.list) || []
      }).catch(() => [])
    )
  ).then(([industryRows, coopRows, franchiseeRows]) => {
    let industryOptions = mapEnumRowsToOptions(industryRows, false)
    let cooperativePurposeOptions = mapEnumRowsToOptions(coopRows, true)
    let franchiseeTypeOptions = mapEnumRowsToOptions(franchiseeRows, true)
    franchiseeTypeOptions = ensureFranchiseeNoneOption(franchiseeTypeOptions)
    if (!industryOptions.length) industryOptions = [...FALLBACK_INDUSTRY_OPTIONS]
    if (!cooperativePurposeOptions.length) cooperativePurposeOptions = [...FALLBACK_COOPERATIVE_PURPOSE_OPTIONS]
    if (!franchiseeTypeOptions.length) franchiseeTypeOptions = [...FALLBACK_FRANCHISEE_TYPE_OPTIONS]
    return {
      industryOptions,
      cooperativePurposeOptions,
      franchiseeTypeOptions,
    }
  })
}

/** Tab 的 name 需为字符串，与 formInline.cooperative_purpose_type 一致 */
export function cooperativePurposeTabsFromOptions(coopOptions) {
  const src = coopOptions && coopOptions.length ? coopOptions : FALLBACK_COOPERATIVE_PURPOSE_OPTIONS
  return src.map(o => ({ label: o.label, value: String(o.value) }))
}

export function labelByValue(options, value) {
  if (value === '' || value === null || typeof value === 'undefined') return ''
  const item = (options || []).find(o => String(o.value) === String(value))
  return item ? item.label : ''
}

export function displayCooperativePurpose(v, options) {
  const t = labelByValue(options, v)
  if (t) return t
  const n = v === '' || v === null || typeof v === 'undefined' ? null : Number(v)
  if (n !== null && !Number.isNaN(n) && COOP_FALLBACK_MAP[n] != null) return COOP_FALLBACK_MAP[n]
  return v != null && v !== '' ? String(v) : '-'
}

export function displayFranchiseeType(v, options) {
  const t = labelByValue(options, v)
  if (t) return t
  const n = v === '' || v === null || typeof v === 'undefined' ? null : Number(v)
  if (n !== null && !Number.isNaN(n) && FRANCHISEE_FALLBACK_MAP[n] != null) return FRANCHISEE_FALLBACK_MAP[n]
  return v != null && v !== '' ? String(v) : '-'
}

export function displayIndustry(code, options) {
  if (code == null || code === '') return '-'
  const t = labelByValue(options, code)
  if (t) return t
  return code || '-'
}



// WEBPACK FOOTER //
// ./src/utils/supplierEnumOptions.js